import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";

const UpdateListingDaynamic = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const products = location.state?.products || [];

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [updatedData, setUpdatedData] = useState({
    title: [],
    descriptions: [],
    country: "",
    pdfNames: [],
    pdfUrls: [],
    advantages: [],
    availableSizes: [],
    productVideo: [],
    performance: [],
    finishes: [],
    imageBase64: null,
    pdfBase64: [],
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPdfs, setPreviewPdfs] = useState([]); // Updated for multiple PDFs
  const [loading, setLoading] = useState(false);

  if (!products.length) {
    return <p className="text-center text-red-500">⚠ No products found!</p>;
  }

  const handleEditClick = (product) => {
    const productId = product.Productid;
    if (!productId) {
      alert("Product data incomplete (missing ID). Cannot edit.");
      return;
    }

    setSelectedProduct({ ...product, productId });
    setUpdatedData({
      title: product.title || [],
      descriptions: product.descriptions || [],
      country: product.country || "",
      pdfNames: product.pdfNames || [],
      pdfUrls: product.pdfUrls || [],
      advantages: product.advantages || [],
      availableSizes: product.availableSizes || [],
      productVideo: product.productVideo || [],
      performance: product.performance || [],
      finishes: product.finishes || [],
      imageBase64: null,
      pdfBase64: [],
    });
    setPreviewImage(product.imageUrl);
    setPreviewPdfs([]);
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    setPreviewImage(null);
    setPreviewPdfs([]);
    setLoading(false);
  };

  const handleInputChange = (field, index, value) => {
    setUpdatedData((prev) => {
      const updatedField = [...prev[field]];
      updatedField[index] = value;
      return { ...prev, [field]: updatedField };
    });
  };

  const addField = (field) => {
    setUpdatedData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeField = (field, index) => {
    setUpdatedData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleDeleteExistingPdf = (index) => {
    setUpdatedData((prev) => ({
      ...prev,
      pdfUrls: prev.pdfUrls.filter((_, i) => i !== index),
      pdfNames: prev.pdfNames.filter((_, i) => i !== index),
    }));
  };

  const handleProductFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      const base64Data = reader.result.split(",")[1];

      if (type === "image") {
        setUpdatedData((prev) => ({ ...prev, imageBase64: base64Data }));
        setPreviewImage(reader.result);
      } else if (type === "pdf") {
        setUpdatedData((prev) => ({
          ...prev,
          pdfBase64: [...(prev.pdfBase64 || []), base64Data],
        }));
        setPreviewPdfs((prev) => [...prev, reader.result]);
      }
    };
  };

  const handleUpdate = async () => {
    if (!selectedProduct) return;
    setLoading(true);

    try {
      const payload = {
        title: updatedData.title,
        descriptions: updatedData.descriptions,
        country: updatedData.country,
        pdfNames: updatedData.pdfNames,
        pdfUrls: updatedData.pdfUrls,
        advantages: updatedData.advantages,
        availableSizes: updatedData.availableSizes,
        productVideo: updatedData.productVideo,
        performance: updatedData.performance,
        finishes: updatedData.finishes,
        createdAt: selectedProduct.createdAt,
        subcategory: selectedProduct.subcategory,
        category: selectedProduct.category,
        imageBase64: updatedData.imageBase64 || undefined,
        pdfBase64: updatedData.pdfBase64.length ? updatedData.pdfBase64 : undefined,
      };

      const authToken = localStorage.getItem("authToken");

      await axios.put(
        `https://backend.aktcorp.com/product/update/${selectedProduct.Productid}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      navigate("/update-listing");
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      alert(`Error: ${error.response?.data?.message || "Failed to update product"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto rounded-lg p-6 space-y-6">
      <h2 className="text-2xl font-bold text-center text-white">Matching Products</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {products.map((product) => (
          <div
            key={product.Productid}
            className="border p-4 rounded-lg shadow-md bg-gray-100"
          >
            <img
              src={product.imageUrl}
              alt={product.title?.[0]}
              className="w-full h-60 object-contain rounded bg-gray-100"
            />
            <h3 className="text-lg font-semibold mt-2 line-clamp-1 mb-2">
              {product.title?.[0]}
            </h3>
            <div
              className="text-sm text-justify overflow-hidden line-clamp-5"
              dangerouslySetInnerHTML={{ __html: product.descriptions?.[0] || "" }}
            ></div>
            <button
              onClick={() => handleEditClick(product)}
              className="mt-3 w-full bg-green-500 text-white p-2 rounded-md text-sm"
            >
              Edit Product
            </button>
          </div>
        ))}
      </div>

      {selectedProduct && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 text-center">Update Product</h2>

            {/* Image Upload */}
            <label className="block font-medium mb-1">Update Image:</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleProductFileChange(e, "image")}
            />
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-40 object-contain rounded mt-2 bg-gray-100"
              />
            )}

            {/* PDF Upload */}
            <label className="block font-medium mb-1 mt-4">Add PDF File:</label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => handleProductFileChange(e, "pdf")}
            />
            {previewPdfs.length > 0 && (
              <div className="mt-2 space-y-1 text-xs text-blue-600">
                {previewPdfs.map((pdf, index) => (
                  <a
                    key={index}
                    href={pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate"
                  >
                    Preview PDF {index + 1}
                  </a>
                ))}
              </div>
            )}

            {/* Existing PDFs */}
            {updatedData.pdfNames.length >= 0 && updatedData.pdfUrls.length >= 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">Existing PDFs:</h3>
                <ul className="space-y-1 max-h-40 overflow-y-auto text-sm">
                  {updatedData.pdfNames.map((name, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center bg-gray-100 px-2 py-1 rounded"
                    >
                      <a
                        href={updatedData.pdfUrls[index]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 truncate max-w-[60%]"
                      >
                        {name || `PDF ${index + 1}`}
                      </a>
                      <button
                        onClick={() => handleDeleteExistingPdf(index)}
                        className="text-red-500 text-xs ml-2"
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Text Inputs */}
            {[
              { label: "Title", field: "title" },
              { label: "Descriptions", field: "descriptions" },
              { label: "PDF Names", field: "pdfNames" },
              { label: "Advantages", field: "advantages" },
              { label: "Available Sizes", field: "availableSizes" },
              { label: "Product Videos", field: "productVideo" },
              { label: "Performance", field: "performance" },
              { label: "Finishes", field: "finishes" },
            ].map(({ label, field }) => (
              <div key={field} className="mt-4">
                <label className="block text-lg font-medium text-gray-700">{label}:</label>
                {updatedData[field].map((item, index) => (
                  <div key={index} className="relative mb-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleInputChange(field, index, e.target.value)}
                      className="w-full p-2 border rounded-md"
                    />
                    {index > 0 && (
                      <button
                        onClick={() => removeField(field, index)}
                        className="absolute top-0 right-0 bg-red-500 text-white px-2 py-1 rounded-md text-xs"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addField(field)}
                  className="bg-blue-500 text-white p-2 rounded-md w-full mt-2"
                >
                  + Add More {label}
                </button>
              </div>
            ))}

            {/* Country Select */}
            <div className="mt-4">
              <label className="block text-lg font-medium text-gray-700">Country:</label>
              <select
                value={updatedData.country}
                onChange={(e) =>
                  setUpdatedData((prev) => ({ ...prev, country: e.target.value }))
                }
                className="w-full p-2 border rounded-md"
              >
                <option value="">Choose a Country</option>
                <option value="All Country">All Country</option>
                <option value="North America">North America</option>
                <option value="Latin America">Latin America</option>
                <option value="Europe, Middle East & Africa">
                  Europe, Middle East & Africa
                </option>
                <option value="India & Pakistan">India & Pakistan</option>
                <option value="Southeast Asia">Southeast Asia</option>
                <option value="Australia & New Zealand">Australia & New Zealand</option>
                <option value="Japan">Japan</option>
                <option value="China">China</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between mt-4">
              <button
                onClick={handleCancel}
                className="bg-red-500 text-white px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateListingDaynamic;
